/**
 * ResultsOverlay - Fullscreen V65 results with blue/gold broadcast theme
 */

import { TravparkLogo } from './TravparkLogo';

export function ResultsOverlay({ resultsVisible, resultsData, sommertravetMode }) {
  if (!resultsVisible || !resultsData) return null;

  const legs = resultsData.legs || [];
  const payout = resultsData.payout || {};
  const bongTips = resultsData.bong_tips || {};
  const date = resultsData.date ? new Date(resultsData.date).toLocaleDateString('nb-NO', {
    day: 'numeric', month: 'long', year: 'numeric'
  }) : '';

  const hasBong = Object.values(bongTips).some(v => v && v.trim());

  const correctCount = legs.filter(leg => {
    const tips = (bongTips[leg.leg] || '').split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
    return tips.includes(leg.winner_number);
  }).length;

  return (
    <div
      data-testid="results-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_37d71e0f-ea96-4348-a27f-5a06f0067365/artifacts/ywps6pb9_file_00000000df9c7243b3636a21f3a725db.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 90px 90px',
        boxSizing: 'border-box',
        zIndex: 50,
      }}
    >
      <div style={{
        animation: 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '16px',
        border: '3px solid var(--theme-panel-border)',
        backgroundColor: '#050F23',
        boxShadow: `0 0 50px rgba(var(--theme-accent-rgb), 0.2), 0 25px 50px rgba(0,0,0,0.5)`,
        width: '100%',
        maxWidth: '1100px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        isolation: 'isolate',
      }}>

        {/* Header */}
        <div style={{
          
          background: 'linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)',
          borderBottom: '3px solid var(--theme-panel-border)',
          color: '#FFFFFF',
          padding: '20px 36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <TravparkLogo height={90} sommertravetMode={sommertravetMode} />
            <div>
              <div style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: '2.4rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.08em',
                lineHeight: 1.1, color: 'var(--theme-accent)',
              }}>
                {resultsData.pool_type} Resultater
              </div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '1.1rem', color: 'rgba(255,255,255,0.6)',
                marginTop: '4px',
              }}>
                Sørlandets Travpark &bull; {date}
              </div>
            </div>
          </div>
          {hasBong && (
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1.6rem',
              color: correctCount === legs.length ? '#4ADE80' : 'var(--theme-accent)',
              fontWeight: 700, textAlign: 'right',
            }}>
              {correctCount}/{legs.length} riktige
            </div>
          )}
        </div>

        {/* Column headers */}
        {hasBong && (
          <div style={{
            
            display: 'flex', alignItems: 'center',
            padding: '10px 36px',
            borderBottom: '2px solid rgba(var(--theme-accent-rgb), 0.2)',
          }}>
            <div style={{ width: '80px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: 'var(--theme-accent)', textTransform: 'uppercase' }}>Avd.</div>
            <div style={{ width: '50px' }} />
            <div style={{ flex: 1, marginLeft: '16px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: 'var(--theme-accent)', textTransform: 'uppercase' }}>Vinner</div>
            <div style={{ width: '180px', textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--theme-accent)', textTransform: 'uppercase' }}>Dine Tips</div>
            <div style={{ width: '56px' }} />
          </div>
        )}

        {/* Results rows */}
        <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
          {legs.map((leg, index) => {
            const tips = (bongTips[leg.leg] || '').split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
            const isCorrect = tips.length > 0 && tips.includes(leg.winner_number);
            const hasTip = tips.length > 0;

            return (
              <div
                key={leg.leg}
                style={{
                  display: 'flex', alignItems: 'center',
                  padding: '16px 36px',
                  borderBottom: index < legs.length - 1 ? '1px solid rgba(var(--theme-accent-rgb), 0.08)' : 'none',
                  backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                  animation: 'none',
                }}
              >
                <div style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '1.3rem', fontWeight: 700, color: 'var(--theme-accent)',
                  width: '80px', textTransform: 'uppercase',
                }}>
                  {resultsData.pool_type}-{leg.leg}
                </div>

                <div style={{
                  width: '50px', height: '50px',
                  backgroundColor: 'var(--theme-accent)', color: '#0A1628',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: 700, fontSize: '1.5rem', borderRadius: '8px', flexShrink: 0,
                }}>
                  {leg.winner_number}
                </div>

                <div style={{ flex: 1, marginLeft: '16px', overflow: 'hidden' }}>
                  <div style={{
                    fontFamily: "'Inter', sans-serif", fontWeight: 600,
                    fontSize: '1.5rem', color: '#FFFFFF',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2,
                  }}>
                    {leg.winner_name}
                  </div>
                  <div style={{
                    fontFamily: "'Inter', sans-serif", fontSize: '1rem', color: 'rgba(255,255,255,0.5)',
                  }}>
                    {leg.winner_driver}
                  </div>
                </div>

                {hasBong && (
                  <>
                    <div style={{
                      width: '180px', textAlign: 'center',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '1.6rem', fontWeight: 700, color: '#FFFFFF',
                    }}>
                      {hasTip ? tips.join(', ') : '-'}
                    </div>

                    <div style={{
                      width: '56px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      {hasTip && (
                        isCorrect ? (
                          <div style={{
                            width: '38px', height: '38px', borderRadius: '50%',
                            backgroundColor: 'var(--theme-accent)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            animation: 'none',
                          }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0A1628" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </div>
                        ) : (
                          <div style={{
                            width: '38px', height: '38px', borderRadius: '50%',
                            backgroundColor: '#DC2626',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            animation: 'none',
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Payout footer */}
        <div style={{
          
          borderTop: '3px solid var(--theme-panel-border)',
          background: 'linear-gradient(135deg, rgba(var(--theme-accent-rgb), 0.1) 0%, transparent 100%)',
          padding: '18px 36px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '32px', flexShrink: 0,
        }}>
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '1rem', color: 'rgba(255,255,255,0.5)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Utbetaling
          </div>
          {payout.splits && payout.splits.map((split) => (
            <div key={split.correct} style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '1.3rem', color: 'var(--theme-accent)', fontWeight: 700,
            }}>
              {split.correct} riktige: {Math.round(split.amount).toLocaleString('nb-NO')} kr
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
